import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import FomoFiLogo from "@/components/brand/FomoFiLogo";
import SidebarNavItem from "@/components/layout/SidebarNavItem";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NAV_ITEMS } from "@/config/navItems";

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

// The profile dropdown that used to live in the sidebar footer moved to the
// upper-right ProfileMenu (feedback #5/#6); the "Zero net sum" promo tile was
// removed (feedback #7).
export default function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  return (
    <TooltipProvider>
      <aside
        className={cn(
          "hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 bg-sidebar text-sidebar-foreground rounded-r-2xl transition-all duration-300",
          collapsed ? "w-[72px]" : "w-64",
        )}
      >
        {/* Logo + collapse toggle */}
        <div
          className={cn(
            "flex h-16 items-center shrink-0 border-b border-sidebar-border",
            collapsed ? "justify-center px-0" : "justify-between px-5",
          )}
        >
          <FomoFiLogo size="md" showText={!collapsed} />
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-sidebar-foreground/40 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Nav items */}
        <nav
          aria-label="Main navigation"
          className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}
        >
          <ul role="list" className="flex flex-col gap-1.5">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <SidebarNavItem
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  collapsed={collapsed}
                />
              </li>
            ))}
          </ul>
        </nav>

        {/* FP-181: the feedback stack and Back to top dock here from lg (index.css), so
            the menu above ends where they begin, at every window height. */}
        <div aria-hidden="true" className="h-[var(--sidebar-fab-reserve)] shrink-0" />
      </aside>
    </TooltipProvider>
  );
}
